import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { getClients, deleteClient, getClientStats, getStorageUsage, cleanupOldClients, clearAllClientData } from '../../utils/clientStorage';
import toast from 'react-hot-toast';

const ClientGallery = () => {
  const { isDark } = useTheme();
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterShape, setFilterShape] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [storageUsage, setStorageUsage] = useState(null);

  // Load clients on component mount
  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = () => {
    const clientsData = getClients();
    setClients(clientsData);
    setStats(getClientStats());
    setStorageUsage(getStorageUsage());
  };

  const handleDeleteClient = (clientId, clientName) => {
    if (window.confirm(`Are you sure you want to delete ${clientName}'s analysis?`)) {
      try {
        deleteClient(clientId);
        loadClients();
        toast.success(`Deleted ${clientName}'s analysis`);
      } catch (error) {
        toast.error('Failed to delete client analysis');
      }
    }
  };

  const handleCleanupStorage = () => {
    if (window.confirm('This will remove old client records to free up storage space. Continue?')) {
      try {
        cleanupOldClients();
        loadClients();
        toast.success('Storage cleanup completed');
      } catch (error) {
        toast.error('Failed to cleanup storage');
      }
    }
  };

  const handleClearAllData = () => {
    if (window.confirm('⚠️ WARNING: This will delete ALL client data permanently! This cannot be undone. Are you absolutely sure?')) {
      if (window.confirm('This is your last chance. Click OK to permanently delete ALL client data.')) {
        try {
          clearAllClientData();
          loadClients();
          toast.success('All client data cleared');
        } catch (error) {
          toast.error('Failed to clear all data');
        }
      }
    }
  };

  const handleViewClient = (client) => {
    setSelectedClient(client);
  };

  const closeClientDetails = () => {
    setSelectedClient(null);
  };

  // Filter and sort clients
  const filteredClients = clients
    .filter(client => {
      const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           client.phone.includes(searchTerm) ||
                           client.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesShape = !filterShape || client.analysisData?.shape === filterShape;
      return matchesSearch && matchesShape;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'name':
          return a.name.localeCompare(b.name);
        case 'shape':
          return (a.analysisData?.shape || '').localeCompare(b.analysisData?.shape || '');
        default:
          return 0;
      }
    });

  const uniqueShapes = [...new Set(clients.map(c => c.analysisData?.shape).filter(Boolean))];

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} py-8`}>
      <div className=" px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Client Analysis Gallery
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            View and manage all client face analysis and hairstyle generations
          </p>
        </div>

        {/* Statistics */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
              <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total Clients</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
              <div className="text-2xl font-bold text-green-600">{stats.withAnalysis}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">With Analysis</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
              <div className="text-2xl font-bold text-purple-600">{stats.withGeneration}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">With Hairstyles</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
              <div className="text-2xl font-bold text-orange-600">{stats.recentClients}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">This Week</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
              <div className="text-2xl font-bold text-red-600">{storageUsage?.usedMB || '0'}MB</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Storage Used</div>
            </div>
          </div>
        )}

        {/* Filters and Search */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Search Clients
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, phone, or email"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Filter by Shape
              </label>
              <select
                value={filterShape}
                onChange={(e) => setFilterShape(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Shapes</option>
                {uniqueShapes.map(shape => (
                  <option key={shape} value={shape}>
                    {shape.charAt(0).toUpperCase() + shape.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Sort By
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="name">Name A-Z</option>
                <option value="shape">Face Shape</option>
              </select>
            </div>
            <div className="flex items-end space-x-2">
              <button
                onClick={loadClients}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                🔄 Refresh
              </button>
              <button
                onClick={handleCleanupStorage}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                title="Clean up old records to free storage space"
              >
                🗑️ Cleanup
              </button>
              <button
                onClick={handleClearAllData}
                className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                title="⚠️ EMERGENCY: Clear ALL client data"
              >
                ⚠️ Clear All
              </button>
            </div>
          </div>
        </div>

        {/* Clients Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClients.map((client) => (
            <div key={client.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              {/* Client Image */}
              <div className="relative">
                <img
                  src={client.image}
                  alt={`${client.name} analysis`}
                  className="w-full h-48 object-cover"
                />
                <div className="absolute top-2 right-2">
                  <span className="bg-blue-600 text-white px-2 py-1 rounded-full text-xs font-medium">
                    {client.analysisData?.shape?.charAt(0).toUpperCase() + client.analysisData?.shape?.slice(1) || 'Unknown'}
                  </span>
                </div>
                {/* Generated Hairstyle Preview */}
                {client.generationData?.outputImage && (
                  <div className="absolute bottom-2 left-2">
                    <img
                      src={client.generationData.outputImage}
                      alt={`Generated hairstyle for ${client.name}`}
                      className="w-16 h-16 object-cover rounded-lg border-2 border-white shadow-lg"
                    />
                  </div>
                )}
              </div>

              {/* Client Info */}
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {client.name}
                </h3>
                
                <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  {client.phone && (
                    <div className="flex items-center space-x-2">
                      <span>📞</span>
                      <span>{client.phone}</span>
                    </div>
                  )}
                  {client.email && (
                    <div className="flex items-center space-x-2">
                      <span>📧</span>
                      <span>{client.email}</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-2">
                    <span>📅</span>
                    <span>{new Date(client.createdAt).toLocaleDateString()}</span>
                  </div>
                  {client.analysisData && (
                    <div className="flex items-center space-x-2">
                      <span>⏱️</span>
                      <span>Analysis: {client.analysisData.analysisTime}s</span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-2 mt-4">
                  <button
                    onClick={() => handleViewClient(client)}
                    className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >
                    👁️ View Details
                  </button>
                  <button
                    onClick={() => handleDeleteClient(client.id, client.name)}
                    className="px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredClients.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500 dark:text-gray-400">
              <div className="text-6xl mb-4">📷</div>
              <h3 className="text-xl font-semibold mb-2">No clients found</h3>
              <p>Try adjusting your search or filters</p>
            </div>
          </div>
        )}

        {/* Client Details Modal */}
        {selectedClient && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {selectedClient.name} - Analysis Details
                  </h2>
                  <button
                    onClick={closeClientDetails}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    ✕
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Client Image */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Original Photo</h3>
                    <img
                      src={selectedClient.image}
                      alt={`${selectedClient.name} analysis`}
                      className="w-full rounded-lg shadow"
                    />
                  </div>

                  {/* Analysis Data */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Analysis Results</h3>
                    <div className="space-y-4">
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <h4 className="font-medium mb-2">Face Shape Analysis</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span>Shape:</span>
                            <span className="font-medium">
                              {selectedClient.analysisData?.shape?.charAt(0).toUpperCase() + selectedClient.analysisData?.shape?.slice(1) || 'Unknown'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Confidence:</span>
                            <span className="font-medium">{selectedClient.analysisData?.confidence || 'High'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Analysis Time:</span>
                            <span className="font-medium">{selectedClient.analysisData?.analysisTime}s</span>
                          </div>
                        </div>
                      </div>

                      {/* Metrics */}
                      {selectedClient.analysisData?.metrics && (
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                          <h4 className="font-medium mb-2">Face Measurements</h4>
                          <div className="space-y-1 text-sm">
                            {Object.entries(selectedClient.analysisData.metrics).map(([key, value]) => (
                              <div key={key} className="flex justify-between">
                                <span className="capitalize">{key.replace(/_/g, ' ')}:</span>
                                <span className="font-medium">
                                  {typeof value === 'number' ? value.toFixed(2) : value}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Generation Data */}
                      {selectedClient.generationData && (
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                          <h4 className="font-medium mb-2">Hairstyle Generation</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span>Status:</span>
                              <span className="font-medium">{selectedClient.generationData.status}</span>
                            </div>
                            {selectedClient.generationData.promptUsed && (
                              <div className="flex justify-between">
                                <span>Prompt Used:</span>
                                <span className="font-medium text-xs">{selectedClient.generationData.promptUsed}</span>
                              </div>
                            )}
                            {selectedClient.generationData.generationTime && (
                              <div className="flex justify-between">
                                <span>Generation Time:</span>
                                <span className="font-medium">{selectedClient.generationData.generationTime}s</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Generated Hairstyle */}
                {(selectedClient.generationData?.outputImage || selectedClient.generationData?.outputUrl) && (
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold mb-3">Generated Hairstyle</h3>
                    <div className="text-center">
                      <img
                        src={selectedClient.generationData.outputImage || selectedClient.generationData.outputUrl}
                        alt={`Generated hairstyle for ${selectedClient.name}`}
                        className="w-full max-w-md mx-auto rounded-lg shadow"
                      />
                    </div>
                  </div>
                )}

                {/* Contact Info */}
                <div className="mt-6 bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="font-medium mb-2">Contact Information</h4>
                  <div className="space-y-2 text-sm">
                    {selectedClient.phone && (
                      <div className="flex items-center space-x-2">
                        <span>📞</span>
                        <span>{selectedClient.phone}</span>
                      </div>
                    )}
                    {selectedClient.email && (
                      <div className="flex items-center space-x-2">
                        <span>📧</span>
                        <span>{selectedClient.email}</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <span>📅</span>
                      <span>Created: {new Date(selectedClient.createdAt).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span>🔄</span>
                      <span>Updated: {new Date(selectedClient.updatedAt).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientGallery;
